import type { CatalogApiDatasource } from '../datasources/catalog-api.datasource';
import type {
  Paginated,
  PaginationInput,
  Product,
  CreateProductInput,
  UpdateProductStatusInput,
  UpdateProductExternalKeyInput,
  ProductExternalKeyInput,
  ProductPerk,
  ApplyProductPerkBatchInput,
  ProductPerkBatchResult,
  PerkGroup,
  PerkHolderCountGroup,
  PerkHolderCount,
  ResolvedPlatformProfile,
  GrantPolicySpec,
  PerkPreset,
  PerkPresetItem,
  CreatePerkPresetInput,
  ApplyPerkPresetItemBatchInput,
  PerkPresetItemBatchResult,
  CatalogRole,
  Feature,
  UpdateScopesInput,
} from '../../domain/entities/catalog.entity';

export class CatalogRepositoryImpl {
  private api: CatalogApiDatasource;

  constructor(api: CatalogApiDatasource) {
    this.api = api;
  }

  // ---- Products ----------------------------------------------------------

  async listProducts(input?: PaginationInput): Promise<Paginated<Product>> {
    return this.api.listProducts(input);
  }

  async createProduct(input: CreateProductInput): Promise<Product> {
    return this.api.createProduct(input);
  }

  async updateProductStatus(
    productId: string,
    input: UpdateProductStatusInput,
  ): Promise<Product> {
    return this.api.updateProductStatus(productId, input);
  }

  async updateProductExternalKey(
    productId: string,
    input: UpdateProductExternalKeyInput,
  ): Promise<Product> {
    return this.api.updateProductExternalKey(productId, input);
  }

  async findProductByExternalKey(
    key: ProductExternalKeyInput,
  ): Promise<Product | null> {
    return this.api.findProductByExternalKey(key);
  }

  async listProductPerks(productId: string): Promise<ProductPerk[]> {
    return this.api.listProductPerks(productId);
  }

  async applyProductPerkBatch(
    productId: string,
    input: ApplyProductPerkBatchInput,
  ): Promise<ProductPerkBatchResult> {
    return this.api.applyProductPerkBatch(productId, input);
  }

  // ---- Perks -------------------------------------------------------------

  async listPerkGroups(input?: PaginationInput): Promise<Paginated<PerkGroup>> {
    return this.api.listPerkGroups(input);
  }

  async listAllPerkGroups(): Promise<PerkGroup[]> {
    return this.api.listAllPerkGroups();
  }

  async listGrantPolicySpecs(): Promise<GrantPolicySpec[]> {
    return this.api.listGrantPolicySpecs();
  }

  // ---- Portadores de perk ------------------------------------------------

  async countPerkHolders(
    groups: PerkHolderCountGroup[],
  ): Promise<PerkHolderCount[]> {
    return this.api.countPerkHolders(groups);
  }

  async listPerkHolderProfiles(
    perkIds: string[],
    input?: PaginationInput,
  ): Promise<Paginated<{ platformProfileId: string }>> {
    return this.api.listPerkHolderProfiles(perkIds, input);
  }

  async resolvePlatformProfiles(
    platformProfileIds: string[],
  ): Promise<ResolvedPlatformProfile[]> {
    return this.api.resolvePlatformProfiles(platformProfileIds);
  }

  // ---- Perk presets ------------------------------------------------------

  async listPerkPresets(
    input?: PaginationInput,
  ): Promise<Paginated<PerkPreset>> {
    return this.api.listPerkPresets(input);
  }

  async listAllPerkPresets(): Promise<PerkPreset[]> {
    return this.api.listAllPerkPresets();
  }

  async createPerkPreset(input: CreatePerkPresetInput): Promise<PerkPreset> {
    return this.api.createPerkPreset(input);
  }

  async updatePerkPreset(
    presetId: string,
    input: CreatePerkPresetInput,
  ): Promise<PerkPreset> {
    return this.api.updatePerkPreset(presetId, input);
  }

  async deletePerkPreset(presetId: string): Promise<PerkPreset> {
    return this.api.deletePerkPreset(presetId);
  }

  async listPerkPresetItems(presetId: string): Promise<PerkPresetItem[]> {
    return this.api.listPerkPresetItems(presetId);
  }

  async applyPerkPresetItemBatch(
    presetId: string,
    input: ApplyPerkPresetItemBatchInput,
  ): Promise<PerkPresetItemBatchResult> {
    return this.api.applyPerkPresetItemBatch(presetId, input);
  }

  // ---- Roles -------------------------------------------------------------

  async listRoles(
    input?: PaginationInput & { includeDeprecated?: boolean },
  ): Promise<Paginated<CatalogRole>> {
    return this.api.listRoles(input);
  }

  async updateRoleScopes(
    roleId: string,
    input: UpdateScopesInput,
  ): Promise<CatalogRole> {
    return this.api.updateRoleScopes(roleId, input);
  }

  // ---- Features ----------------------------------------------------------

  async listFeatures(
    input?: PaginationInput & { includeDeprecated?: boolean },
  ): Promise<Paginated<Feature>> {
    return this.api.listFeatures(input);
  }

  async updateFeatureScopes(
    featureId: string,
    input: UpdateScopesInput,
  ): Promise<Feature> {
    return this.api.updateFeatureScopes(featureId, input);
  }
}
